/**
 * @fileoverview Piece Interaction System - ピース操作システム
 * ドラッグ&ドロップ、スワップ処理、アニメーション連携を統合
 */

import { ROWS, COLS, PIECE_SIZE, GameStatus } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { runCascadeFromRemovals } from '../../gameplay/cascade.js';
import { getGameBoard, getActiveLimitDriver } from '../../gameplay/game-controls.js';
import { getMatchingPieces } from '../../gameplay/matches.js';
import { applySpecialPieceRules } from '../../gameplay/specials/manager.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { canInteract } from '../../shared/utils/game-state-utils.js';
import { DRAG_CONSTANTS } from '../constants.js';
import {
  animateReturnWithShake,
  animateSwapUI,
  animateMergeUI,
} from '../handlers/user-input-helpers.js';
import { notifyBoardMatched, shouldBlockSwap } from '../tutorial/tutorial-guards.js';

import { getDragState } from './drag-state.js';

//-------------
// DOMユーティリティ（インライン化）
//-------------
function getCellElementOrNull(row, col) {
  if (!Number.isInteger(row) || !Number.isInteger(col)) return null;
  if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return null;

  const element = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
  return element instanceof HTMLElement ? element : null;
}

function dedupePiecesByPosition(pieces = []) {
  const unique = [];
  const seen = new Set();
  for (const piece of pieces) {
    if (!piece || !Array.isArray(piece.position)) continue;
    const [row, col] = piece.position || [];
    if (!Number.isInteger(row) || !Number.isInteger(col)) continue;
    const key = `${row}:${col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(piece);
  }
  return unique;
}

function longestRunInGroups(groups) {
  let longest = 0;
  groups.forEach((coords) => {
    const sorted = Array.from(new Set(coords)).sort((a, b) => a - b);
    if (sorted.length === 0) {
      return;
    }
    let streak = 1;
    longest = Math.max(longest, 1);
    for (let index = 1; index < sorted.length; index += 1) {
      if (sorted[index] === sorted[index - 1] + 1) {
        streak += 1;
      } else {
        streak = 1;
      }
      if (streak > longest) {
        longest = streak;
      }
    }
  });
  return longest;
}

function analyzeStandardMatch(primaryMatches = [], secondaryMatches = []) {
  const uniquePieces = dedupePiecesByPosition([...primaryMatches, ...secondaryMatches]);
  const totalCount = uniquePieces.length;
  if (totalCount === 0) {
    return { longestRun: 0, orientation: null, totalCount: 0 };
  }

  const rows = new Map();
  const cols = new Map();
  uniquePieces.forEach((piece) => {
    const [row, col] = piece.position;
    if (!rows.has(row)) {
      rows.set(row, []);
    }
    rows.get(row).push(col);
    if (!cols.has(col)) {
      cols.set(col, []);
    }
    cols.get(col).push(row);
  });

  const longestHorizontal = longestRunInGroups(rows);
  const longestVertical = longestRunInGroups(cols);

  let orientation = null;
  const longestRun = Math.max(longestHorizontal, longestVertical);
  if (longestRun >= 3) {
    if (longestHorizontal >= 3 && longestVertical >= 3) {
      orientation = 'complex';
    } else if (longestVertical > longestHorizontal) {
      orientation = 'vertical';
    } else {
      orientation = 'horizontal';
    }
  }

  return {
    longestRun,
    orientation,
    totalCount,
  };
}

async function resolveSuccessfulSwap({
  gameBoard,
  piece1,
  piece2,
  preSwapPos1,
  preSwapPos2,
  pieceMatches,
  newPieceMatches,
}) {
  const pieceForAnim1 = { position: preSwapPos1, specialType: piece1.specialType };
  const pieceForAnim2 = { position: preSwapPos2, specialType: piece2.specialType };

  await new Promise((resolve) => {
    animateSwapUI(pieceForAnim1, pieceForAnim2, async () => {
      try {
        const combinedMatches = await applySpecialPieceRules(
          gameBoard,
          [pieceMatches, newPieceMatches],
          [piece1, piece2],
        );

        [piece1, piece2].forEach((p) => {
          if (p.specialType) {
            combinedMatches.push(p);
          }
        });

        const analysis = analyzeStandardMatch(pieceMatches, newPieceMatches);
        const specialCreated = combinedMatches.some((piece) => piece && piece._pendingCreation);
        const matchDetails = {
          matchSize: analysis.longestRun,
          totalCleared: analysis.totalCount,
          orientation: analysis.orientation,
          specialCreated,
          triggeredSpecial: Boolean(piece1.specialType || piece2.specialType),
          mix: false,
        };

        await runCascadeFromRemovals(gameBoard, combinedMatches);
        notifyBoardMatched(matchDetails);
      } finally {
        resolve();
      }
    });
  });
}

//-------------
// PieceInteractionSystemメインクラス
//-------------
export const PieceInteractionSystem = {
  // 状態管理
  lastInvalidSwapTime: 0,

  /**
   * スワップ処理の完全実装
   */
  handleDisallowedSwap(preSwapPos) {
    const cellElement = getCellElementOrNull(preSwapPos[0], preSwapPos[1]);
    if (cellElement) {
      try {
        cellElement.style.transform = 'translate(0px, 0px)';
        cellElement.removeAttribute('data-x');
        cellElement.removeAttribute('data-y');
        animateReturnWithShake(cellElement);
      } catch (error) {
        MinimalErrorTracker.info('PIECE_INTERACTION', 'Swap rejection animation failed');
      }
    }
    this.setInvalidSwapTime();
    gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready);
  },
  async executeSwap(piece1, piece2) {
    if (!this.validateSwap(piece1, piece2)) return;

    // 同時操作を防ぐため処理中かチェック
    if (!canInteract(gameState)) {
      return; // 既に処理中の場合はスキップ
    }

    try {
      // スワップ操作全体の開始時に処理状態を設定
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Processing);

      // 位置保存
      const preSwapPos1 = [...piece1.position];
      const preSwapPos2 = [...piece2.position];

      const from = { row: preSwapPos1[0], col: preSwapPos1[1] };
      const to = { row: preSwapPos2[0], col: preSwapPos2[1] };

      if (shouldBlockSwap(from, to)) {
        this.handleDisallowedSwap(preSwapPos1);
        return;
      }
      // 特殊×特殊はMix判定（スワップ前に判定）
      if (piece1.specialType && piece2.specialType) {
        await this.executeMixSwap(piece1, piece2, preSwapPos1, preSwapPos2);
        consumeMoveIfNeeded(); // 成功Mix
        return;
      }

      // スワップ実行（通常のSwap処理）
      this.swapPieces(piece1, piece2);

      // マッチ判定
      const gameBoard = getGameBoard();
      const pieceMatches = getMatchingPieces(gameBoard, piece1);
      const newPieceMatches = getMatchingPieces(gameBoard, piece2);

      // マッチまたは特殊ピースがある場合
      if (
        pieceMatches.length > 0 ||
        newPieceMatches.length > 0 ||
        piece1.specialType ||
        piece2.specialType
      ) {
        consumeMoveIfNeeded(); // 成功スワップ

        await resolveSuccessfulSwap({
          gameBoard,
          piece1,
          piece2,
          preSwapPos1,
          preSwapPos2,
          pieceMatches,
          newPieceMatches,
        });
      } else {
        this.revertSwap(piece1, piece2, preSwapPos1, preSwapPos2);
      }
    } finally {
      // スワップ操作全体完了後にReady状態にリセット
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready);

      // Classic mode GameOverチェック（Ready状態で実行）
      // NOTE: time-limitは「Ready中のみ時間進行」のため、常にReady状態でGameOverになる
      // move-limitもこれに合わせ、Ready復帰時にコールバックを発火する
      const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);
      if (phase === 'consumed') {
        const callback = gameState.getState(STATE_PATHS.LIMIT_ON_LIMIT_REACHED);
        if (typeof callback === 'function') {
          callback(); // gameOver() が Ready 状態で呼ばれる
        }
      }
    }
  },

  /**
   * Mix実行処理
   */
  async executeMixSwap(piece1, piece2, preSwapPos1, preSwapPos2) {
    const { resolveMix } = await import('../../gameplay/specials/mix-resolver.js');
    const mixPiece = resolveMix(piece1, piece2);

    if (!mixPiece) {
      // Mix不可の組み合わせ → リバート
      this.revertSwap(piece1, piece2, preSwapPos1, preSwapPos2);
      return;
    }

    const gameBoard = getGameBoard();
    const { getBoardRenderer } = await import('../../graphics/renderers/board-renderer.js');
    const renderer = getBoardRenderer();
    const previewRow = mixPiece.position[0];
    const previewCol = mixPiece.position[1];

    renderer.beginPreviewAt(previewRow, previewCol, mixPiece.specialType);

    try {
      await new Promise((resolve) => {
        const pieceForAnim1 = { position: preSwapPos1, specialType: piece1.specialType };
        const pieceForAnim2 = { position: preSwapPos2, specialType: piece2.specialType };

        // mixPiece.specialTypeを渡して合成演出を実行
        animateMergeUI(
          pieceForAnim1,
          pieceForAnim2,
          mixPiece.position,
          mixPiece.specialType,
          () => {
            resolve();
          },
        );
      });

      // ボード更新: 元の2ピースを削除してMixピースを配置
      gameBoard.removePiece(preSwapPos1[0], preSwapPos1[1]);
      gameBoard.removePiece(preSwapPos2[0], preSwapPos2[1]);
      gameBoard.setPiece(previewRow, previewCol, mixPiece);

      // BoardRenderer経由でDOM更新（特殊エフェクトが正しいDOM状態で実行されるように）
      renderer.updatePieceAt(previewRow, previewCol, mixPiece);

      // DOM反映を待機（RAF）
      await new Promise((r) => requestAnimationFrame(r));

      // Mix特殊ピースを即座に発動
      await runCascadeFromRemovals(gameBoard, [mixPiece]);
      notifyBoardMatched({
        matchSize: 0,
        specialCreated: false,
        triggeredSpecial: true,
        mix: true,
      });
    } finally {
      renderer.endPreviewAt(previewRow, previewCol);
    }
  },

  /**
   * バリデーション
   */
  validateSwap(piece1, piece2) {
    if (!piece1 || !piece2) return false;

    const ui = gameState.getState(STATE_PATHS.UI);
    if (ui?.isSkillTargeting) return false;
    if (!canInteract(gameState)) return false;

    if (!this.isAdjacent(piece1, piece2)) return false;
    if (!this.isInBounds(piece1.position) || !this.isInBounds(piece2.position)) return false;

    return true;
  },

  /**
   * スワップ実行
   */
  swapPieces(piece1, piece2) {
    const [row1, col1] = piece1.position;
    const [row2, col2] = piece2.position;
    const gameBoard = getGameBoard();
    gameBoard.swapPieces(row1, col1, row2, col2);
  },

  /**
   * スワップリバート
   */
  revertSwap(piece1, piece2, preSwapPos1, preSwapPos2) {
    this.swapPieces(piece1, piece2);

    try {
      const el1 = getCellElementOrNull(preSwapPos1[0], preSwapPos1[1]);
      const el2 = getCellElementOrNull(preSwapPos2[0], preSwapPos2[1]);
      if (el1) animateReturnWithShake(el1);
      if (el2) animateReturnWithShake(el2);
    } catch (error) {
      MinimalErrorTracker.warn('PIECE_INTERACTION', 'Revert animation failed', error);
    }

    // 注意: swapAnimIdクリーンアップとui.isInputBlockedはexecuteSwapのfinallyブロックで処理
    gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready);
  },

  /**
   * ドロップ処理
   */
  async handleDrop(event) {
    const target = event.target;
    const dx = parseFloat(target.getAttribute('data-x') || 0);
    const dy = parseFloat(target.getAttribute('data-y') || 0);

    // 共有ドラッグ状態を取得
    const drag = getDragState();
    const lockedDx = drag.axis === 'y' ? 0 : dx;
    const lockedDy = drag.axis === 'x' ? 0 : dy;

    const { row, col, newRow, newCol } = this.getNewPosition(target, lockedDx, lockedDy);

    const gameBoard = getGameBoard();
    const piece = gameBoard.getPiece(row, col);
    if (!piece) {
      this.resetDragAttributes(target);
      return;
    }

    const isInBounds = newRow >= 0 && newRow < ROWS && newCol >= 0 && newCol < COLS;
    const isAdjacentMove = Math.abs(newRow - row) + Math.abs(newCol - col) === 1;

    if (!isInBounds || !isAdjacentMove) {
      animateReturnWithShake(target);
      return;
    }

    const newPiece = gameBoard.getPiece(newRow, newCol);
    if (!newPiece) {
      animateReturnWithShake(target);
      return;
    }

    // Transformクリーンアップ
    try {
      const shadow = target.closest('.piece-shadow');
      const inner = shadow?.querySelector('.piece');
      if (inner) {
        inner.style.transform = '';
        inner.style.transition = '';
      }
    } catch (error) {
      MinimalErrorTracker.info('PIECE_INTERACTION', 'Transform cleanup failed');
    }

    this.executeSwap(piece, newPiece);
  },

  /**
   * ユーティリティメソッド
   */
  getNewPosition(target, dx, dy) {
    const col = parseInt(target.getAttribute('data-col') || 0, 10);
    const row = parseInt(target.getAttribute('data-row') || 0, 10);
    const stepY = Math.abs(dy) >= PIECE_SIZE / 4 ? Math.sign(dy) : 0;
    const stepX = Math.abs(dx) >= PIECE_SIZE / 4 ? Math.sign(dx) : 0;
    const newRow = row + stepY;
    const newCol = col + stepX;
    return { row, col, newRow, newCol };
  },

  resetDragAttributes(target) {
    // ドラッグリセット
    target.style.transform = 'translate(0px, 0px)';
    ['data-x', 'data-y', 'data-start-x', 'data-start-y'].forEach((attr) =>
      target.removeAttribute(attr),
    );
  },

  isAdjacent(piece1, piece2) {
    const [row1, col1] = piece1.position;
    const [row2, col2] = piece2.position;
    return (
      (Math.abs(row1 - row2) === 1 && col1 === col2) ||
      (Math.abs(col1 - col2) === 1 && row1 === row2)
    );
  },

  isInBounds([row, col]) {
    return row >= 0 && row < ROWS && col >= 0 && col < COLS;
  },

  hasInputCooldown() {
    return Date.now() - this.lastInvalidSwapTime < DRAG_CONSTANTS.INVALID_SWAP_COOLDOWN;
  },

  setInvalidSwapTime() {
    this.lastInvalidSwapTime = Date.now();
  },
};

/**
 * モードがClassicの場合のみ手数を消費
 */
function consumeMoveIfNeeded() {
  const driver = getActiveLimitDriver();
  if (driver && driver.getType && driver.getType() === 'move') {
    if (driver.decrementMove) {
      driver.decrementMove(gameState);
    }
  }
}

// 後方互換性のためのエイリアス
export const DragSystem = PieceInteractionSystem;
